// Notification service for tracking request status changes
const NOTIFICATIONS_KEY = 'acom_notifications';

// Notification types
export const NotificationType = {
  STATUS_CHANGE: 'status_change',
  NEW_REQUEST: 'new_request',
  APPROVAL_NEEDED: 'approval_needed',
  NEW_COMMENT: 'new_comment',
};

// Get all notifications for a user
export const getNotifications = (userEmail) => {
  const allNotifications = JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '[]');
  return allNotifications
    .filter(n => n.userEmail === userEmail)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
};

// Get unread notifications count
export const getUnreadCount = (userEmail) => {
  const notifications = getNotifications(userEmail);
  return notifications.filter(n => !n.read).length;
};

// Create a new notification
export const createNotification = (notification) => {
  const allNotifications = JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '[]');
  const newNotification = {
    id: Date.now().toString(),
    ...notification,
    read: false,
    createdAt: new Date().toISOString(),
  };
  
  allNotifications.push(newNotification);
  localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(allNotifications));
  
  return newNotification;
};

// Mark notification as read
export const markAsRead = (notificationId) => {
  const allNotifications = JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '[]');
  const notification = allNotifications.find(n => n.id === notificationId);
  
  if (notification) {
    notification.read = true;
    localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(allNotifications));
  }
};

// Mark all notifications as read for a user
export const markAllAsRead = (userEmail) => {
  const allNotifications = JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '[]');
  allNotifications.forEach(n => {
    if (n.userEmail === userEmail) {
      n.read = true;
    }
  });
  localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(allNotifications));
};

// Delete old notifications (older than 7 days)
export const cleanupOldNotifications = () => {
  const allNotifications = JSON.parse(localStorage.getItem(NOTIFICATIONS_KEY) || '[]');
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
  
  const recentNotifications = allNotifications.filter(n => 
    new Date(n.createdAt) > sevenDaysAgo
  );
  
  localStorage.setItem(NOTIFICATIONS_KEY, JSON.stringify(recentNotifications));
};

// Create notification for status change
export const notifyStatusChange = (request, oldStatus, newStatus, userEmail) => {
  // Don't notify the person who made the change
  if (request.submittedBy !== userEmail) {
    createNotification({
      type: NotificationType.STATUS_CHANGE,
      userEmail: request.submittedBy,
      title: `Request Status Updated`,
      message: `Your ${request.type} request "${request.title}" status changed from ${oldStatus} to ${newStatus}`,
      requestId: request.id,
      requestType: request.type,
      metadata: {
        oldStatus,
        newStatus,
        requestTitle: request.title,
      },
    });
  }
};

// Create notification for new approval needed
export const notifyApprovalNeeded = (request, approverEmail) => {
  createNotification({
    type: NotificationType.APPROVAL_NEEDED,
    userEmail: approverEmail,
    title: 'New Request Pending Approval',
    message: `A new ${request.type} request from ${request.submittedBy} requires your approval`,
    requestId: request.id,
    requestType: request.type,
    metadata: {
      submittedBy: request.submittedBy,
      requestTitle: request.title,
    },
  });
};

// Create notification for new comment
export const notifyNewComment = (request, comment, authorEmail) => {
  // Determine who should be notified
  const recipientsToNotify = new Set();
  
  // Always notify the requester (unless they're the comment author)
  if (request.submittedBy !== authorEmail) {
    recipientsToNotify.add(request.submittedBy);
  }
  
  // Notify approvers (unless they're the comment author)
  if (request.approver && request.approver !== authorEmail) {
    recipientsToNotify.add(request.approver);
  }
  
  // For academic changes with multiple approvers
  if (request.approvers && Array.isArray(request.approvers)) {
    request.approvers.forEach(approver => {
      if (approver !== authorEmail) {
        recipientsToNotify.add(approver);
      }
    });
  }
  
  // Create notifications for each recipient
  recipientsToNotify.forEach(recipientEmail => {
    createNotification({
      type: NotificationType.NEW_COMMENT,
      userEmail: recipientEmail,
      title: 'New Comment on Request',
      message: `${comment.author} added a comment on ${request.type === 'academic' ? 'Academic Change' : 'Extension'} request "${request.title}"`,
      requestId: request.id,
      requestType: request.type,
      metadata: {
        commentAuthor: comment.author,
        commentPreview: comment.content.substring(0, 100) + (comment.content.length > 100 ? '...' : ''),
        requestTitle: request.title,
      },
    });
  });
};